import { AddToCalendarButton } from 'add-to-calendar-button-react';
import type { Event } from '@/lib/types/database';
import {
  formatDateForCalendar,
  formatTimeForCalendar,
} from '@/lib/utils/events.utils';

interface AddEventToCalendarProps {
  event: Event;
}

/**
 * Reusable component for adding events to calendar.
 * Wraps add-to-calendar-button-react with consistent styling
 * and handles date/time formatting from Event objects.
 */
export function AddEventToCalendar({ event }: AddEventToCalendarProps) {
  // Get user's local timezone
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // iOS Safari silently no-ops the library's default `data:text/calendar` URL
  // for the Apple/iCal option. Point it at a hosted HTTPS `.ics` instead, which
  // triggers iOS's native "Add to Calendar" sheet. See supabase/functions/event-ics.
  const icsFile = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/event-ics?id=${event.id}`;

  return (
    <AddToCalendarButton
      name={event.title}
      description={event.description || ''}
      startDate={formatDateForCalendar(event.start_time)}
      startTime={formatTimeForCalendar(event.start_time)}
      endDate={formatDateForCalendar(event.end_time)}
      endTime={formatTimeForCalendar(event.end_time)}
      timeZone={userTimezone}
      location={event.location || ''}
      icsFile={icsFile}
      options={['Apple', 'Google', 'iCal', 'Outlook.com', 'Yahoo']}
      buttonStyle="text"
      listStyle="modal"
      lightMode="dark"
      hideCheckmark
      hideBranding
      styleDark="--btn-background: transparent; --btn-text: #918f8e; --btn-text-hover: #d3bfed; --font: 'Field Gothic', ui-sans-serif, sans-serif; --btn-shadow: none; --btn-shadow-hover: none; --list-shadow: none; --list-modal-shadow: none; --btn-underline: none;"
    />
  );
}
